import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "./icons";
import { Avatar } from "./ui";
import { useWorkspace } from "../app/workspace";
import { useAuth } from "../app/auth";
import { timeAgo } from "../lib/format";
import type { Notification } from "../lib/types";
import * as work from "../lib/workClient";

type NavItem = { to: string; label: string; icon: string; end?: boolean; cap?: string; key?: string };
type NavSection = { id: string; label?: string; pinned?: boolean; defaultCollapsed?: boolean; items: NavItem[] };

/* Nav is grouped into collapsible sections. Each item declares the capability
 * that gates it; a section with no visible items disappears entirely, so a
 * CSM (no config capabilities) never sees the Configure section. */
const SECTIONS: NavSection[] = [
  { id: "pinned", pinned: true, items: [
    { to: "/", label: "Command Center", icon: "grid", end: true },
    { to: "/copilot", label: "Copilot", icon: "sparkle" },
    { to: "/cockpit", label: "Cockpit", icon: "target" },
  ] },
  { id: "customers", label: "Customers", items: [
    { to: "/accounts", label: "Accounts", icon: "building", cap: "customers" },
    { to: "/relationships", label: "Relationships", icon: "link", cap: "customers" },
    { to: "/success-plans", label: "Success Plans", icon: "target", cap: "customers" },
  ] },
  { id: "insights", label: "Signals & Insights", items: [
    { to: "/signals", label: "Signals", icon: "pulse", cap: "insights", key: "signals" },
    { to: "/predictions", label: "Predictions", icon: "sparkle", cap: "insights" },
    { to: "/risk-agents", label: "Risk Agents", icon: "sparkle", cap: "insights" },
    { to: "/scorecards", label: "CSM Scorecards", icon: "activity", cap: "team_analytics" },
    { to: "/dashboards", label: "Dashboards", icon: "activity", cap: "insights" },
  ] },
  { id: "execution", label: "Execution", items: [
    { to: "/tasks", label: "Tasks", icon: "check", cap: "execution" },
    { to: "/playbooks", label: "Playbooks", icon: "route", cap: "execution" },
    { to: "/automations", label: "Automations", icon: "bolt", cap: "execution" },
  ] },
  { id: "revenue", label: "Revenue & Voice", items: [
    { to: "/renewals", label: "Renewals", icon: "refresh", cap: "revenue" },
    { to: "/surveys", label: "Surveys", icon: "star", cap: "revenue" },
    { to: "/qbr", label: "QBR Builder", icon: "doc", cap: "revenue" },
  ] },
  { id: "configure", label: "Configure", defaultCollapsed: true, items: [
    { to: "/health-designer", label: "Health Designer", icon: "target", cap: "settings" },
    { to: "/task-types", label: "Task Types", icon: "check", cap: "settings" },
    { to: "/segments", label: "Segments", icon: "segment", cap: "settings" },
    { to: "/delivery", label: "Slack & Teams", icon: "bell", cap: "settings" },
    { to: "/integrations", label: "Integrations", icon: "plug", cap: "integrations" },
    { to: "/data", label: "Data Studio", icon: "database", cap: "integrations" },
    { to: "/import", label: "Import CSV", icon: "arrowDown", cap: "integrations" },
    { to: "/developers", label: "Developers", icon: "route", cap: "developers" },
    { to: "/roles", label: "Roles", icon: "user", cap: "governance" },
    { to: "/permissions", label: "Permission Groups", icon: "lock", cap: "governance" },
  ] },
];

const LS_NAV_COLLAPSE = "penumbrai:navCollapsed";
function readCollapsed(): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem(LS_NAV_COLLAPSE) || "{}"); } catch { return {}; }
}

function BrandMark() {
  return (
    <svg viewBox="0 0 40 40" width="26" height="26" aria-hidden="true">
      <defs>
        <radialGradient id="rc" cx="50%" cy="50%" r="50%">
          <stop offset="55%" stopColor="#F2A73B" stopOpacity="0" />
          <stop offset="80%" stopColor="#F2A73B" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#F2A73B" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="20" cy="20" r="18" fill="url(#rc)" />
      <circle cx="20" cy="20" r="11" fill="#0C0D13" stroke="#F2A73B" strokeWidth="1.4" />
      <circle cx="24.5" cy="16" r="11" fill="#0C0D13" />
    </svg>
  );
}

export function Shell() {
  const { ws, can } = useWorkspace();
  const loc = useLocation();
  const [cmd, setCmd] = useState(false);
  const [navOpen, setNavOpen] = useState(false); // mobile drawer
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(readCollapsed);
  const openSignals = ws.signals.filter((s) => s.status === "open").length;
  const riskSignals = ws.signals.filter((s) => s.band === "risk" && s.status !== "resolved").length;
  // per-item badge counts bubble up to the section header when collapsed
  const itemCount = (n: NavItem): { n: number; hot: boolean } | null =>
    n.key === "signals" && openSignals > 0 ? { n: openSignals, hot: riskSignals > 0 } : null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmd((v) => !v);
      }
      if (e.key === "Escape") setCmd(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close the mobile nav drawer whenever the route changes.
  useEffect(() => { setNavOpen(false); }, [loc.pathname]);

  const toggle = (id: string) => {
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !isCollapsed(id, prev) };
      try { localStorage.setItem(LS_NAV_COLLAPSE, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const isCollapsed = (id: string, state = collapsed) => {
    const sec = SECTIONS.find((s) => s.id === id);
    return id in state ? state[id] : !!sec?.defaultCollapsed;
  };

  // filter each section to the items the current user can see
  const visibleSections = SECTIONS
    .map((s) => ({ ...s, items: s.items.filter((i) => !i.cap || can(i.cap)) }))
    .filter((s) => s.items.length > 0);

  return (
    <div className={`shell ${navOpen ? "shell--nav-open" : ""}`}>
      {navOpen && <div className="rail-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />}
      <aside className={`rail ${navOpen ? "rail--open" : ""}`}>
        <div className="rail__brand">
          <BrandMark />
          <b>Penumbrai</b>
          <button className="rail__close" onClick={() => setNavOpen(false)} aria-label="Close menu">
            <Icon name="close" size={18} />
          </button>
        </div>

        {visibleSections.map((sec) => {
          if (sec.pinned) {
            return (
              <nav key={sec.id} className="rail__group">
                {sec.items.map((n) => <NavItemLink key={n.to} n={n} count={itemCount(n)} />)}
              </nav>
            );
          }
          // the section holding the current route auto-expands and stays open
          const active = sec.items.some((n) => n.to !== "/" && loc.pathname.startsWith(n.to));
          const open = active || !isCollapsed(sec.id);
          // when collapsed, surface the sum of any hidden item badges on the header
          const hidden = sec.items.map(itemCount).filter(Boolean) as { n: number; hot: boolean }[];
          const total = hidden.reduce((a, c) => a + c.n, 0);
          const hot = hidden.some((c) => c.hot);
          return (
            <div key={sec.id} className={`rail__sec ${active ? "has-active" : ""}`}>
              <button className={`rail__sechd ${open ? "" : "collapsed"}`} onClick={() => toggle(sec.id)} aria-expanded={open}>
                <span className="caret"><Icon name="chevron" size={13} /></span>
                <span className="sec-label">{sec.label}</span>
                {!open && total > 0 && <span className={`sec-count ${hot ? "hot" : ""}`}>{total}</span>}
              </button>
              {open && sec.items.map((n) => <NavItemLink key={n.to} n={n} count={itemCount(n)} />)}
            </div>
          );
        })}

        <div className="rail__spacer" />

        <AccountMenu wsName={ws.name} />
      </aside>

      <main className="main">
        <Topbar onSearch={() => setCmd(true)} onMenu={() => setNavOpen(true)} />
        <Outlet />
      </main>

      {cmd && <CommandPalette onClose={() => setCmd(false)} />}
    </div>
  );
}

function NavItemLink({ n, count }: { n: NavItem; count: { n: number; hot: boolean } | null }) {
  return (
    <NavLink to={n.to} end={n.end} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
      <Icon name={n.icon} className="ic" />
      {n.label}
      {count && <span className={`count ${count.hot ? "hot" : ""}`}>{count.n}</span>}
    </NavLink>
  );
}

function Topbar({ onSearch, onMenu }: { onSearch: () => void; onMenu: () => void }) {
  return (
    <div className="topbar">
      <button className="topbar__menu" onClick={onMenu} aria-label="Open menu">
        <Icon name="menu" size={20} />
      </button>
      <button className="search" onClick={onSearch} aria-label="Search">
        <Icon name="search" size={15} />
        Search accounts, signals, playbooks…
        <kbd>⌘K</kbd>
      </button>
      <NotificationBell />
      <button className="btn btn--primary btn--sm"><Icon name="plus" size={15} /> New play</button>
    </div>
  );
}

const NTF_ICON: Record<string, string> = { assignment: "check", signal: "pulse", agent: "sparkle", playbook: "route", mention: "user", system: "bell" };

function NotificationBell() {
  const { ws } = useWorkspace();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<work.Mode>("local");
  const [items, setItems] = useState<Notification[]>(ws.notifications);
  useEffect(() => { work.loadNotifications(ws).then((r) => { setMode(r.mode); setItems(r.notifications); }); }, [ws]);
  const unread = items.filter((n) => !n.read).length;

  const openItem = async (n: Notification) => {
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    await work.markNotificationRead(mode, ws, n.id);
    setOpen(false);
    if (n.link) nav(n.link);
  };
  const readAll = async () => { setItems((prev) => prev.map((n) => ({ ...n, read: true }))); await work.markAllNotificationsRead(mode, ws); };

  return (
    <div className="ntf">
      <button className="btn btn--icon" aria-label="Notifications" onClick={() => setOpen((o) => !o)}>
        <Icon name="bell" size={17} />
        {unread > 0 && <span className="ntf__badge">{unread}</span>}
      </button>
      {open && (
        <>
          <div className="acct-scrim" onClick={() => setOpen(false)} />
          <div className="ntf__panel">
            <div className="ntf__hd">
              <b>Notifications</b>
              {unread > 0 && <button className="linklike" onClick={readAll}>Mark all read</button>}
            </div>
            <div className="ntf__list">
              {items.length === 0 ? <div className="empty">You're all caught up.</div> : items.map((n) => (
                <button key={n.id} className={`ntf__item ${n.read ? "" : "unread"}`} onClick={() => openItem(n)}>
                  <span className="ntf__ic"><Icon name={NTF_ICON[n.kind] ?? "bell"} size={14} /></span>
                  <span style={{ minWidth: 0 }}>
                    <span className="ntf__title">{n.title}</span>
                    {n.body && <span className="ntf__body">{n.body}</span>}
                    <span className="ntf__at">{timeAgo(n.at)} ago</span>
                  </span>
                  {!n.read && <span className="ntf__dot" />}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function CommandPalette({ onClose }: { onClose: () => void }) {
  const { ws, can } = useWorkspace();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);

  // pages are drawn from the same capability-gated nav sections, so the palette
  // can't jump a user to a page their permission group hides
  const pages = SECTIONS.flatMap((s) => s.items)
    .filter((i) => !i.cap || can(i.cap))
    .map((i) => ({ label: i.label, to: i.to, icon: i.icon }));

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    const acct = ws.accounts
      .filter((a) => !s || a.name.toLowerCase().includes(s) || a.industry.toLowerCase().includes(s) || a.region.toLowerCase().includes(s))
      .slice(0, 6)
      .map((a) => ({ group: "Accounts", label: a.name, sub: `${a.industry} · ${a.region}`, to: `/accounts/${a.id}`, icon: "building" }));
    // Contacts across accounts — jump to their account.
    const contacts = !s ? [] : ws.accounts
      .flatMap((a) => a.contacts.map((c) => ({ c, a })))
      .filter(({ c }) => c.name.toLowerCase().includes(s) || c.title.toLowerCase().includes(s) || c.email.toLowerCase().includes(s))
      .slice(0, 4)
      .map(({ c, a }) => ({ group: "Contacts", label: c.name, sub: `${c.title} · ${a.name}`, to: `/accounts/${a.id}`, icon: "user" }));
    const signals = !s ? [] : ws.signals
      .filter((sg) => sg.title.toLowerCase().includes(s) || sg.accountName.toLowerCase().includes(s) || sg.detail.toLowerCase().includes(s))
      .slice(0, 4)
      .map((sg) => ({ group: "Signals", label: sg.title, sub: sg.accountName, to: `/accounts/${sg.accountId}`, icon: "pulse" }));
    const tasks = !s ? [] : ws.tasks
      .filter((t) => t.title.toLowerCase().includes(s) || t.accountName.toLowerCase().includes(s))
      .slice(0, 4)
      .map((t) => ({ group: "Tasks", label: t.title, sub: t.accountName, to: `/tasks`, icon: "check" }));
    const playbooks = !s ? [] : ws.playbooks
      .filter((p) => p.name.toLowerCase().includes(s) || p.description.toLowerCase().includes(s))
      .slice(0, 4)
      .map((p) => ({ group: "Playbooks", label: p.name, sub: p.category, to: `/playbooks`, icon: "route" }));
    const pg = pages.filter((p) => !s || p.label.toLowerCase().includes(s)).map((p) => ({ ...p, group: "Go to", sub: "" }));
    return [...pg, ...acct, ...contacts, ...signals, ...tasks, ...playbooks];
  }, [q, ws]);

  const go = (to: string) => { nav(to); onClose(); };

  return (
    <div className="cmdk-overlay" onClick={onClose}>
      <div className="cmdk" onClick={(e) => e.stopPropagation()}>
        <input
          autoFocus
          placeholder="Jump to an account, page, or play…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setSel(0); }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") setSel((s) => Math.min(s + 1, results.length - 1));
            if (e.key === "ArrowUp") setSel((s) => Math.max(s - 1, 0));
            if (e.key === "Enter" && results[sel]) go(results[sel].to);
          }}
        />
        <div className="cmdk__list">
          {results.length === 0 && <div className="empty">No matches</div>}
          {results.map((r, i) => {
            const showGroup = i === 0 || results[i - 1].group !== r.group;
            return (
              <div key={r.to + i}>
                {showGroup && <div className="cmdk__group">{r.group}</div>}
                <div className={`cmdk__item ${i === sel ? "sel" : ""}`} onMouseEnter={() => setSel(i)} onClick={() => go(r.to)}>
                  <Icon name={r.icon} size={16} />
                  <span className="cmdk__label">{r.label}</span>
                  {r.sub && <span className="cmdk__sub">{r.sub}</span>}
                  {i === sel && <span className="k">↵</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AccountMenu({ wsName }: { wsName: string }) {
  const { me, logout } = useAuth();
  const { ws, me: effUser, groups, viewAsUserId, setViewAs } = useWorkspace();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const user = me?.user;
  const plan = me?.plan;
  const impersonating = !!viewAsUserId && viewAsUserId !== ws.currentUserId;
  const groupName = (id?: string) => groups.find((g) => g.id === id)?.name ?? "No group";

  const switchView = (userId: string) => {
    setViewAs(userId === ws.currentUserId ? null : userId);
    setOpen(false);
    // land somewhere the new persona can always see, so a guard can't bounce us
    nav("/");
  };

  return (
    <div className="rail__acct">
      {open && (
        <>
          <div className="acct-scrim" onClick={() => setOpen(false)} />
          <div className="acct-menu">
            <div className="acct-menu__hd">
              <Avatar name={user?.name ?? "?"} />
              <div style={{ minWidth: 0 }}>
                <div className="acct-menu__name">{user?.name}</div>
                <small>{user?.email}</small>
              </div>
            </div>
            <div className="acct-menu__plan">
              <span>{plan?.name} plan</span>
              {plan?.status === "trialing" && <span className="badge badge--watch">Trial</span>}
              {plan?.status === "free" && <span className="badge badge--neutral">Free</span>}
            </div>

            {/* Demo "view as" — preview the app with another group's access */}
            <div className="acct-menu__section">View as <span className="faint">(demo)</span></div>
            <div className="acct-menu__viewas">
              <select className="inp" value={effUser.id} onChange={(e) => switchView(e.target.value)}>
                {ws.users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} — {groupName(u.groupId)}{u.id === ws.currentUserId ? " (you)" : ""}</option>
                ))}
              </select>
              {impersonating && <button className="linklike" style={{ marginTop: 6 }} onClick={() => switchView(ws.currentUserId)}>Reset to yourself</button>}
            </div>

            <Link to="/settings" className="acct-menu__item" onClick={() => setOpen(false)}><Icon name="settings" size={15} /> Settings &amp; billing</Link>
            <Link to="/settings" className="acct-menu__item" onClick={() => setOpen(false)}><Icon name="user" size={15} /> Invite your team</Link>
            <button className="acct-menu__item" onClick={async () => { setOpen(false); await logout(); nav("/"); }}><Icon name="external" size={15} /> Sign out</button>
          </div>
        </>
      )}
      {impersonating && (
        <div className="rail__viewas-banner">
          <Icon name="lock" size={12} /> Viewing as {effUser.name}
          <button className="linklike" onClick={() => switchView(ws.currentUserId)}>Exit</button>
        </div>
      )}
      <button className="rail__me" onClick={() => setOpen((o) => !o)}>
        <Avatar name={user?.name ?? "?"} />
        <div style={{ minWidth: 0, textAlign: "left", flex: 1 }}>
          <div className="acct-menu__name" style={{ fontSize: 13 }}>{user?.name}</div>
          <small>{wsName}</small>
        </div>
        <span className="rail__me-caret"><Icon name="arrowUp" size={12} /></span>
      </button>
    </div>
  );
}
